import { warnAndNotify } from '@execonline-inc/error-reporting.private';
import { putQueryParam, toUrlT, URLParser } from '@execonline-inc/url';
import { assertNever } from '@kofno/piper';
import { Task } from 'taskarian';
import { callApi } from '../../../../Appy';
import { currentUserStore } from '../../../../CurrentUserStore';
import { handleAppyError } from '../../../../ErrorHandling';
import { findLinkT } from '../../../../LinkyLinky';
import { participantsResourceDecoder } from '../../../../ParticipantsStore/Decoders';
import { ParticipantsError } from '../../../../ParticipantsStore/Reactions';
import { Link } from '../../../../Resource/Types';
import { TPlainTextKey } from '../../../../Translations';
import ParticipantsV2Store from '.';
import { PER_PAGE, Query, Sort } from './Types';

const errorMessage = (error: ParticipantsError): TPlainTextKey => {
  switch (error.kind) {
    case 'missing-link-error':
    case 'invalid-url-error':
      warnAndNotify('PeopleV2ParticipantsReactions', error.kind, error);
      return 'We are unable to process your request at this time';
    default:
      return handleAppyError(error);
  }
};

const putText =
  (text: string) =>
  (url: URLParser): URLParser =>
    text.trim().length > 0 ? putQueryParam('participants_filter[query]', text, url) : url;

const putSort =
  (sort: Sort) =>
  (url: URLParser): URLParser => {
    switch (sort.kind) {
      case 'server-default':
        return url;
      case 'sorted':
        return putQueryParam(
          'participants_filter[sort_direction]',
          sort.direction,
          putQueryParam('participants_filter[sort_by]', sort.column, url),
        );
      default:
        return assertNever(sort);
    }
  };

const buildUrl =
  (query: Query) =>
  (link: Link): Task<ParticipantsError, Link> =>
    toUrlT(link.href)
      .map(putText(query.text))
      .map(putSort(query.sort))
      .map(putQueryParam('page', String(query.page)))
      .map(putQueryParam('per_page', String(PER_PAGE)))
      .map((url): Link => ({ ...link, href: url.href }));

export const load = (store: ParticipantsV2Store, query: Query) =>
  Task.succeed<ParticipantsError, ReadonlyArray<Link>>(currentUserStore.links)
    .andThen(findLinkT('participants'))
    .andThen(buildUrl(query))
    .andThen(callApi(participantsResourceDecoder, {}))
    .fork(
      (error) => store.failed(query, errorMessage(error)),
      (resource) => store.ready(query, resource),
    );
